import React, { useRef, useState, useEffect } from "react"
import styled, { css } from "styled-components"
import { GatsbyImage } from "gatsby-plugin-image"
import getVideoId from 'get-video-id';
import ReactPlayer from "react-player"
import Slider from "react-slick"
import { Row, Col } from "reactstrap"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faChevronLeft, faChevronRight} from "@fortawesome/free-solid-svg-icons"
import ContainerMax from "components/shared/ContainerMax"
import { media } from "utils/Media"
import playBtn from "images/play.svg"

const Wrap = styled.div`

  ${props => props.backgroundColour === "blue" && css`
    background: ${props => props.theme.colors.primary};
    color: white;

    h1, h2, h3, h4, h5, p, a {
      color: white;
    }
  `}


  h1, h2 {
    font-size: ${props => props.theme.font.h1.size};
  }
`

const Placeholder = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 2;
  transition: all .4s ease;
  opacity: 1;
  visibility: visible;
  cursor: pointer;

  img, .gatsby-image-wrapper, .gatsby-image-wrapper-constrained {
    width: 100%;
    height: 100%;
  }

  ${props => props.playing && css`
    opacity: 0;
    visibility: hidden;
  `}

  .play {
    position: absolute;
    z-index: 2;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60px;
    height: 60px;
    box-shadow: 0 0 40px #000;
    border-radius: 50%;
    background: rgba(0, 0, 0, .5);

    @media ${media.md} {
      width: 100px;
      height: 100px;
    }
  }

  &:hover {
    .play {
      background: black;
    }
  }
`

const SliderWrap = styled.div`
  aspect-ratio: 763 / 398;
  position: relative;

  @media ${media.md} {
    aspect-ratio: 763 / 369;
  }

  @media (min-width: 1368px) {
    aspect-ratio: 763 / 429;
  }

  .slick-slider {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 102%;

    @media ${media.md} {
      width: 136%;
    }

    @media (min-width: 1368px) {
      width: 156%;
    }
  }

  ${props => props.count === 1 && css`
    aspect-ratio: auto;

    .slick-slider {
      position: relative;
      height: auto;
      width: 100%;
    }
  `}
`

const VideoWrap = styled.div`
  margin-right: 2rem;

  ${props => props.count === 1 && css`
    margin-right: 0;
  `}
`

const Arrow = styled.button`
  height: 75px;
  width: 75px;
  background-color: ${props => props.theme.colors.secondary};  
  color: white;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 0;
  position: relative;
  font-size: ${props => props.theme.font.size.xl};
  margin: 2rem 1rem 0 1rem;

  @media ${media.md} {
    margin: 1rem 1rem 0 0;
  }

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
  }

  ${props => props.disabled && css`
    opacity: 50%;
  `}
`

export default function VideoSlider({block}) {
  const {
    backgroundColour,
    title,
    text,
    videos
  } = block

  const sliderRef = useRef(null)
  const [slideIndex, setSlideIndex] = useState(0)
  const [videoPlaying, setVideoPlaying] = useState(-1)

  const handleSlide = (index) => {
    setSlideIndex(index)
    setVideoPlaying(-1)
  }

  var settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: videos?.length > 1 ? 1.5 : 1,
    slidesToScroll: 1,
    lazyload: true,
    arrows: false,
    // prevArrow: <PrevButton/>,
    // nextArrow: <NextButton/>
    beforeChange: (oldIndex, newIndex) => handleSlide(newIndex),
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1.05,
        }
      }
    ]
  };

  const Arrows = () => {
    return(
      <div>
        <Arrow onClick={() => sliderRef.current?.slickPrev()} disabled={slideIndex === 0}>
          <span className="sr-only">Previous</span>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Arrow>
        <Arrow onClick={() => sliderRef.current?.slickNext()} disabled={slideIndex === (videos?.length - 1)}>
          <span className="sr-only">Next</span>
          <FontAwesomeIcon icon={faChevronRight} />
        </Arrow>
      </div>
    )
  }

  return(
    <Wrap className={`${backgroundColour === "blue" ? "py-5 my-4" : "py-md-4"}`} backgroundColour={backgroundColour}>
      <ContainerMax 
        maxWidth="1360"
        className="py-4"
      >
        {title && (
          <h2 className="text-center text-uppercase pb-5">{title}</h2>
        )}
        <Row>
          <Col md={5} className="pb-4 pb-md-0">
            {text && (
              <div dangerouslySetInnerHTML={{ __html: text }} />
            )}

            {videos?.length > 1 && (
              <div className="d-none d-md-block">
                <Arrows />
              </div>
            )}
          </Col>
          <Col md={7}>
            <SliderWrap count={videos?.length}>
              <Slider {...settings} ref={sliderRef}>
                {videos?.map((video, i) => {
                  return(
                    <Video 
                      key={`${video.youtubeUrl}`} 
                      video={video} 
                      count={videos?.length} 
                      videoPlaying={videoPlaying}
                      setVideoPlaying={setVideoPlaying}
                      vId={i}
                    />
                  )
                })}
              </Slider>
            </SliderWrap>
            {videos?.length > 1 && (
              <div className="d-flex justify-content-center d-md-none">
                <Arrows />
              </div>
            )}
          </Col>
        </Row>
      </ContainerMax>
    </Wrap>
  )
}

const Video = ({video, count, videoPlaying, setVideoPlaying, vId}) => {
  const {
    placeholderImage,
    youtubeUrl
  } = video

  const { id } = getVideoId(youtubeUrl);
  const [playing, setPlaying] = useState(false)

  useEffect(() => {
    console.log(videoPlaying)
    if (videoPlaying !== vId) setPlaying(false)
  }, [videoPlaying])

  const onClick = () => {
    setPlaying(true)
    setVideoPlaying(vId)
  }

  return(
    <VideoWrap className="position-relative overflow-hidden" count={count}>
      <Placeholder onClick={onClick} playing={playing}>
        {placeholderImage ? (
          <GatsbyImage
            image={placeholderImage?.localFile.childImageSharp.gatsbyImageData}
            altText={placeholderImage?.altText}
          />
        ) : (
          <img src={`https://img.youtube.com/vi/${id}/maxresdefault.jpg`} />
        )}
        <div className="play"><img src={playBtn} alt="play" /></div>
      </Placeholder>
      <ReactPlayer 
        url={youtubeUrl} 
        controls={true} 
        className="embed-responsive embed-responsive-16by9" 
        width="100%" 
        height="auto"
        playing={playing}
        onPause={() => {
          setPlaying(false)
        }}
      />
    </VideoWrap>
  )
}